import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useForm } from 'react-hook-form'
import axios from 'axios'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons'
import { Loader2 } from 'lucide-react'
import { closeConnectionModal, fetchAllPublicKeys } from '../../integrationsSlice'
import { getAxiosDefaultConfig } from '../../../../modules/utils'
import { linkFromChannel } from '../../../../modules/account'
import { baseUrl } from '../../../../modules/urls'
import { Button } from '@/features/shadcn/ui/button'
import { Switch } from '@/features/shadcn/ui/switch'
import {
  selectIsWorkspaceContext,
  selectOrganizations,
} from '../../../../features/organizations/organizationsSlice'
import {
  clearGmcAccountSetupDismissed,
  clearGmcOAuthSessionForNewConnect,
  dismissGmcOAuthSetupModal,
  setGmcActiveSetupConnectionId,
} from './gmcOAuthSession'
import {
  collectGmcRows,
  gmcAccountsUrl,
  saveMerchantCenterAccountId,
  type GmcAccountOption,
} from './gmcAccountSetup'

interface FormValues {
  merchant_center_account_id: string
}

interface Channel {
  id: string
  ppKeys?: Array<{ keyId: string; keyValue: string; tags?: unknown }>
}

interface Props {
  ownerId: string | number
  retrieveAccounts: () => Promise<void>
  channel?: Channel
  allPublicKeys?: Array<{ keyId: string; keyValue?: string; tags?: unknown }>
}

function pickDefaultGmcAccountId(
  accounts: GmcAccountOption[],
  savedAccountId: string
): string {
  if (!accounts.length) return savedAccountId || ''
  if (savedAccountId && accounts.some((a) => a.id === savedAccountId)) {
    return savedAccountId
  }
  return accounts[0].id
}

export default function GoogleMerchantCenterConnectionModalContent({
  ownerId,
  retrieveAccounts,
  channel,
  allPublicKeys,
}: Props) {
  const dispatch = useDispatch<any>()
  const rootIsOwner = useSelector((state: any) => state.root.isOwner)
  const isWorkspaceContext = useSelector(selectIsWorkspaceContext)
  const organizations = useSelector(selectOrganizations)
  const activeOrg = organizations?.find((org: any) => org.is_active)
  const inOrgContext = isWorkspaceContext && !!activeOrg
  const canEditMyAi = inOrgContext ? activeOrg?.role === 'owner' : !!rootIsOwner
  const closeModal = () => {
    dismissGmcOAuthSetupModal()
    dispatch((closeConnectionModal as any)())
  }

  const rows = useMemo(() => collectGmcRows(channel, allPublicKeys), [channel, allPublicKeys])
  const hasConnections = rows.length > 0

  const [selectedConnectionId, setSelectedConnectionId] = useState<string>(
    () => rows[0]?.connectionId || ''
  )
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [gmcAccounts, setGmcAccounts] = useState<GmcAccountOption[]>([])
  const [accountsLoading, setAccountsLoading] = useState(false)
  const [accountsError, setAccountsError] = useState<string | null>(null)
  const [selectedGmcAccountId, setSelectedGmcAccountId] = useState('')
  const [autoSaving, setAutoSaving] = useState(false)
  const autoSaveStartedRef = useRef<string | null>(null)
  const [aiEnabled, setAiEnabled] = useState(true)

  useEffect(() => {
    if (rows.length && !rows.some((r) => r.connectionId === selectedConnectionId)) {
      setSelectedConnectionId(rows[0].connectionId)
    }
  }, [rows, selectedConnectionId])

  const selectedRow = rows.find((r) => r.connectionId === selectedConnectionId) || rows[0]

  useEffect(() => {
    if (selectedRow?.connectionId) {
      setGmcActiveSetupConnectionId(selectedRow.connectionId)
    }
  }, [selectedRow?.connectionId])

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: { merchant_center_account_id: selectedRow?.merchantCenterAccountId || '' },
  })

  useEffect(() => {
    reset({ merchant_center_account_id: selectedRow?.merchantCenterAccountId || '' })
    setSaved(false)
    setSaveError(null)
    autoSaveStartedRef.current = null
  }, [selectedRow?.connectionId, selectedRow?.merchantCenterAccountId, reset])

  const persistMerchantCenterAccountId = useCallback(
    async (connectionId: string, accountId: string, aiEnabledValue: boolean) => {
      const tasks: Promise<unknown>[] = [saveMerchantCenterAccountId(connectionId, accountId)]
      if (canEditMyAi) {
        tasks.push(
          fetch(`${baseUrl}/user/my-ai`, {
            method: 'PUT',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              platform: 'google_merchant_center',
              account_id: connectionId,
              ai_enabled: aiEnabledValue,
            }),
          })
        )
      }
      await Promise.all(tasks)
      clearGmcAccountSetupDismissed(connectionId)
      await retrieveAccounts()
      if (ownerId) {
        dispatch((fetchAllPublicKeys as any)({ ownerId }))
      }
    },
    [canEditMyAi, dispatch, ownerId, retrieveAccounts]
  )

  useEffect(() => {
    const connectionId = selectedRow?.connectionId
    if (!connectionId) {
      setGmcAccounts([])
      setAccountsError(null)
      setSelectedGmcAccountId('')
      return
    }

    let cancelled = false
    setAccountsLoading(true)
    setAccountsError(null)
    setGmcAccounts([])

    axios
      .get(gmcAccountsUrl(connectionId), getAxiosDefaultConfig())
      .then((res) => {
        if (cancelled) return
        const accounts: GmcAccountOption[] = Array.isArray(res.data?.accounts) ? res.data.accounts : []
        const error = res.data?.error ? String(res.data.error) : null
        setGmcAccounts(accounts)
        setAccountsError(error)
      })
      .catch((err: any) => {
        if (cancelled) return
        setGmcAccounts([])
        setAccountsError(
          err?.response?.data?.error || 'Could not load Merchant Center accounts. Enter your account ID manually.'
        )
      })
      .finally(() => {
        if (!cancelled) setAccountsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [selectedRow?.connectionId])

  useEffect(() => {
    setSelectedGmcAccountId(
      pickDefaultGmcAccountId(gmcAccounts, selectedRow?.merchantCenterAccountId || '')
    )
  }, [gmcAccounts, selectedRow?.merchantCenterAccountId])

  useEffect(() => {
    const connectionId = selectedRow?.connectionId
    if (!connectionId || accountsLoading || gmcAccounts.length !== 1) return
    if (selectedRow?.merchantCenterAccountId) return

    const accountId = gmcAccounts[0].id
    const autoSaveKey = `${connectionId}:${accountId}`
    if (autoSaveStartedRef.current === autoSaveKey) return
    autoSaveStartedRef.current = autoSaveKey

    let cancelled = false
    setAutoSaving(true)
    setSaveError(null)
    setSaved(false)

    persistMerchantCenterAccountId(connectionId, accountId, aiEnabled)
      .then(() => {
        if (!cancelled) setSaved(true)
      })
      .catch((err: any) => {
        if (!cancelled) {
          autoSaveStartedRef.current = null
          setSaveError(err?.response?.data?.error || err?.message || 'Failed to save Merchant Center account ID.')
        }
      })
      .finally(() => {
        if (!cancelled) setAutoSaving(false)
      })

    return () => {
      cancelled = true
    }
  }, [
    accountsLoading,
    gmcAccounts,
    persistMerchantCenterAccountId,
    selectedRow?.connectionId,
    selectedRow?.merchantCenterAccountId,
    aiEnabled,
  ])

  const useAccountPicker = gmcAccounts.length > 0
  const singleAccountAutoSaved =
    useAccountPicker &&
    gmcAccounts.length === 1 &&
    !!selectedRow?.merchantCenterAccountId &&
    selectedRow.merchantCenterAccountId === gmcAccounts[0].id
  const singleAccountNeedsAutoSave =
    useAccountPicker && gmcAccounts.length === 1 && !selectedRow?.merchantCenterAccountId
  const showManualFallback = !accountsLoading && !useAccountPicker

  const startOAuth = () => {
    clearGmcOAuthSessionForNewConnect()
    linkFromChannel('google_merchant_center')
  }

  const onSubmit = async ({ merchant_center_account_id }: FormValues) => {
    setSaveError(null)
    setSaved(false)
    const cid = selectedRow?.connectionId
    if (!cid) {
      setSaveError('No connection selected.')
      return
    }
    const accountId = useAccountPicker ? selectedGmcAccountId : String(merchant_center_account_id || '').trim()
    if (!accountId) {
      setSaveError(
        useAccountPicker
          ? 'Select a Merchant Center account.'
          : 'Merchant Center account ID is required.'
      )
      return
    }
    const trimmed = accountId.trim()
    if (!/^\d+$/.test(trimmed.replace(/^accounts\//, '').split('/')[0])) {
      setSaveError('Enter the numeric account ID from your Merchant Center URL (digits only).')
      return
    }
    try {
      await persistMerchantCenterAccountId(cid, trimmed, aiEnabled)
      setSaved(true)
    } catch (err: any) {
      setSaveError(err?.response?.data?.error || err?.message || 'Failed to save Merchant Center account ID.')
    }
  }

  const selectedGmcAccount = gmcAccounts.find((a) => a.id === selectedGmcAccountId)

  return (
    <div className="tw-space-y-5 tw-p-6">
      <div className="tw-space-y-1">
        <h2 className="tw-m-0 tw-text-xl tw-font-semibold tw-tracking-tight tw-text-slate-900">
          Google Merchant Center
        </h2>
        <p className="tw-m-0 tw-text-sm tw-text-slate-500">
          Finish setup so the AI agent can use your Merchant Center account automatically.
        </p>
      </div>

      <div className="tw-rounded-xl tw-border tw-border-blue-100 tw-bg-gradient-to-b tw-from-blue-50/80 tw-to-white tw-p-4 tw-text-sm tw-text-blue-950">
        <p className="tw-mb-2 tw-font-semibold tw-text-blue-900">Connect with Google</p>
        <p className="tw-mb-2 tw-text-blue-900/90">
          Sign in with a Google account that has <strong>Admin</strong> access to your Merchant Center.
          PayHelm uses this for product feed health, disapprovals, promotions, and inventory via the AI
          agent.
        </p>
        <p className="tw-mb-0 tw-text-blue-900/90">
          After connecting, choose your <strong>Merchant Center account</strong> from the list below.
          The agent uses it automatically so you do not have to paste it in every chat.
        </p>
      </div>

      {!hasConnections ? (
        <div className="tw-space-y-3">
          <Button type="button" onClick={startOAuth}>
            Connect Google Merchant Center
          </Button>
          <p className="tw-m-0 tw-text-xs tw-text-slate-500">
            <a
              href="https://merchants.google.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="tw-font-medium tw-text-blue-600 tw-underline-offset-2 hover:tw-underline"
            >
              Open Merchant Center
              <FontAwesomeIcon icon={faExternalLinkAlt} className="tw-ml-1 tw-text-xs" />
            </a>{' '}
            to confirm you have access before connecting.
          </p>
        </div>
      ) : (
        <div className="tw-space-y-4">
          <Button type="button" variant="outline" size="sm" onClick={startOAuth}>
            Connect another Google account
          </Button>

          {rows.length > 1 && (
            <div className="tw-space-y-1.5">
              <label className="tw-block tw-text-sm tw-font-medium tw-text-slate-700">
                Google account
              </label>
              <select
                className="tw-w-full tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-2.5 tw-text-sm tw-text-slate-900 focus:tw-border-blue-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-100"
                value={selectedConnectionId}
                onChange={(e) => setSelectedConnectionId(e.target.value)}
              >
                {rows.map((r) => (
                  <option key={r.connectionId} value={r.connectionId}>
                    {r.email || r.connectionId}
                  </option>
                ))}
              </select>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="tw-space-y-4">
            <div className="tw-space-y-1.5">
              <label className="tw-block tw-text-sm tw-font-medium tw-text-slate-700">
                Merchant Center account
              </label>

              {accountsLoading ? (
                <div className="tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-slate-200 tw-bg-slate-50 tw-px-3 tw-py-3 tw-text-sm tw-text-slate-600">
                  <Loader2 className="tw-h-4 tw-w-4 tw-animate-spin" aria-hidden />
                  Loading Merchant Center accounts…
                </div>
              ) : useAccountPicker && gmcAccounts.length === 1 ? (
                <div className="tw-rounded-lg tw-border tw-border-slate-200 tw-bg-slate-50 tw-px-3 tw-py-3 tw-text-sm tw-text-slate-800">
                  {autoSaving ? (
                    <span className="tw-flex tw-items-center tw-gap-2">
                      <Loader2 className="tw-h-4 tw-w-4 tw-animate-spin" aria-hidden />
                      Saving your account…
                    </span>
                  ) : (
                    <>
                      We found your account:{' '}
                      <strong>
                        {selectedGmcAccount?.name || gmcAccounts[0].name} ({gmcAccounts[0].id})
                      </strong>
                    </>
                  )}
                </div>
              ) : useAccountPicker ? (
                <select
                  className="tw-w-full tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-2.5 tw-text-sm tw-text-slate-900 focus:tw-border-blue-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-100"
                  value={selectedGmcAccountId}
                  onChange={(e) => setSelectedGmcAccountId(e.target.value)}
                >
                  {gmcAccounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.name} ({account.id})
                    </option>
                  ))}
                </select>
              ) : (
                <>
                  <input
                    {...register('merchant_center_account_id', {
                      required: 'Account ID is required',
                    })}
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    placeholder="e.g. 123456789"
                    className="tw-w-full tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-2.5 tw-text-sm focus:tw-border-blue-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-100"
                  />
                  {errors.merchant_center_account_id && (
                    <p className="tw-mt-1 tw-text-xs tw-text-red-500">
                      {errors.merchant_center_account_id.message}
                    </p>
                  )}
                  <p className="tw-mt-1 tw-text-xs tw-text-slate-500">
                    From <code className="tw-text-xs">merchants.google.com/.../accounts/123456789</code>{' '}
                    — use the numeric id only.
                  </p>
                </>
              )}

              {accountsError && showManualFallback && (
                <p className="tw-mt-2 tw-text-xs tw-text-amber-700">{accountsError}</p>
              )}
            </div>

            {saveError && (
              <div className="tw-rounded-lg tw-border tw-border-red-200 tw-bg-red-50 tw-px-3 tw-py-2 tw-text-sm tw-text-red-700">
                {saveError}
              </div>
            )}
            {saved && (
              <p className="tw-m-0 tw-text-sm tw-font-medium tw-text-emerald-700">Account ID saved.</p>
            )}

            {canEditMyAi && (
              <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-px-4 tw-py-3">
                <div className="tw-min-w-0">
                  <div className="tw-text-sm tw-font-medium tw-text-slate-900">Enable AI access</div>
                  <div className="tw-text-xs tw-text-slate-500">
                    Allow the AI agent to use this Merchant Center connection
                  </div>
                </div>
                <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-2.5">
                  <span
                    className={`tw-text-xs tw-font-medium ${aiEnabled ? 'tw-text-blue-700' : 'tw-text-slate-400'}`}
                  >
                    {aiEnabled ? 'On' : 'Off'}
                  </span>
                  <Switch
                    checked={aiEnabled}
                    onCheckedChange={(checked) => setAiEnabled(checked)}
                    className="data-[state=checked]:tw-bg-blue-600"
                    aria-label="Enable AI access for this Merchant Center connection"
                  />
                </div>
              </div>
            )}
            {!canEditMyAi && (
              <p className="tw-m-0 tw-text-xs tw-text-slate-500">
                Only the account owner can enable AI access for this connection (Settings → My AI).
              </p>
            )}

            <div className="tw-flex tw-items-center tw-justify-end tw-gap-2 tw-pt-1">
              <Button type="button" variant="outline" onClick={closeModal}>
                Close
              </Button>
              <Button
                type="submit"
                disabled={
                  isSubmitting ||
                  accountsLoading ||
                  autoSaving ||
                  singleAccountAutoSaved ||
                  (singleAccountNeedsAutoSave && saved)
                }
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="tw-mr-2 tw-h-4 tw-w-4 tw-animate-spin" aria-hidden />
                    Saving…
                  </>
                ) : (
                  'Save account ID'
                )}
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  )
}
